import React, { useState, useEffect } from 'react';
import { Search, Check, X, Eye, MessageSquare } from 'lucide-react';

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    // Simulate API call with mock data
    setTimeout(() => {
      setProviders([
        {
          id: 1,
          name: 'Praha Cleaning Service',
          email: 'info@prahacleaning.cz',
          phone: '+420 123 456 789',
          category: 'Cleaning',
          status: 'pending',
          appliedDate: '2025-01-08',
          documents: ['business_license.pdf', 'insurance.pdf'],
          rating: null,
          completedJobs: 0
        },
        {
          id: 2,
          name: 'Brno Plumbing Masters',
          email: 'contact@brnoplumbing.cz',
          phone: '+420 987 654 321',
          category: 'Plumbing',
          status: 'approved',
          appliedDate: '2024-12-15',
          documents: ['business_license.pdf', 'insurance.pdf', 'certifications.pdf'],
          rating: 4.8,
          completedJobs: 156
        },
        {
          id: 3,
          name: 'Ostrava Electric Solutions',
          email: 'hello@ostravaelectric.cz',
          phone: '+420 555 123 456',
          category: 'Electrical',
          status: 'rejected',
          appliedDate: '2025-01-05',
          documents: ['business_license.pdf'],
          rating: null,
          completedJobs: 0,
          rejectionReason: 'Incomplete documentation'
        }
      ]);
      setLoading(false);
    }, 1000);
  };

  const handleStatusChange = async (providerId, newStatus) => {
    // Simulate API call
    setProviders(providers.map(provider =>
      provider.id === providerId
        ? { ...provider, status: newStatus }
        : provider
    ));
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || provider.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const StatusBadge = ({ status }) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Provider Management</h1>
          <p className="text-gray-600 mt-1">Review and manage service provider applications</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <span className="text-sm text-gray-500">
            {filteredProviders.length} providers found
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search providers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Providers List */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Provider</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Category</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Applied</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Rating</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProviders.map((provider) => (
                  <tr key={provider.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{provider.name}</div>
                        <div className="text-sm text-gray-500">{provider.email}</div>
                        <div className="text-sm text-gray-500">{provider.phone}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-900">{provider.category}</span>
                    </td>
                    <td className="py-4 px-4">
                      <StatusBadge status={provider.status} />
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-900">{provider.appliedDate}</span>
                    </td>
                    <td className="py-4 px-4">
                      {provider.rating ? (
                        <div className="flex items-center">
                          <span className="text-sm font-medium">{provider.rating}</span>
                          <span className="text-sm text-gray-500 ml-1">({provider.completedJobs} jobs)</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No rating yet</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          title="Send Message"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </button>
                        {provider.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(provider.id, 'approved')}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Approve"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleStatusChange(provider.id, 'rejected')}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Reject"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Providers;